import React, {useState, useEffect} from 'react';
import '../css/Music.css';

const Music = () => {
    const [songs, setSongs] = useState([]);
    const [currentSong, setCurrentSong] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditingText, setIsEditingText] = useState(false);

    // Состояния фильтрации и поиска
    const [searchQuery, setSearchQuery] = useState('');
    const [filterLanguage, setFilterLanguage] = useState('All');

    const [fontSize, setFontSize] = useState(16);
    const [showChords, setShowChords] = useState(true);
    const [isDeleteMode, setIsDeleteMode] = useState(false);

    const [title, setTitle] = useState('');
    const [language, setLanguage] = useState('RU');
    const [blocks, setBlocks] = useState([{type: 'verse', text: ''}]);

    const BASE_URL = process.env.REACT_APP_API_URL || 'https://church.info-roman-b.workers.dev';

    useEffect(() => { fetchMusic(); }, []);

    const fetchMusic = async () => {
        try {
            const res = await fetch(`${BASE_URL}/music`);
            const data = await res.json();
            setSongs(Array.isArray(data) ? data : []);
        } catch (e) { console.error("Ошибка загрузки музыки", e); }
    };

    // Логика фильтрации
    const filteredSongs = songs.filter(song => {
        const matchesSearch = song.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesLang = filterLanguage === 'All' || song.language === filterLanguage;
        return matchesSearch && matchesLang;
    });

    const handleStartEditing = () => {
        setTitle(currentSong.title);
        setLanguage(currentSong.language);
        const restoredBlocks = currentSong.sections.map(section => ({
            type: section.type,
            text: section.content
                .filter(item => !item.isChord)
                .map(item => item.text)
                .join('')
        }));
        setBlocks(restoredBlocks);
        setIsEditingText(true);
    };

    const handleSaveEdit = async () => {
        if (!title || blocks.some(b => !b.text)) return alert("Заполните все поля");
        const updatedSong = {
            ...currentSong,
            title,
            language,
            sections: blocks.map(b => ({
                type: b.type,
                content: b.text.split(/(\s+)/).map(word => ({
                    text: word,
                    isSpace: /\s/.test(word)
                }))
            }))
        };
        const updatedSongs = songs.map(s => s.id === currentSong.id ? updatedSong : s);
        await saveToDatabase(updatedSongs);
        setCurrentSong(updatedSong);
        setIsEditingText(false);
    };

    const addBlockField = () => setBlocks([...blocks, {type: 'verse', text: ''}]);
    const updateBlock = (index, field, value) => {
        const newBlocks = [...blocks];
        newBlocks[index][field] = value;
        setBlocks(newBlocks);
    };

    const handleCreateSong = async () => {
        if (!title || blocks.some(b => !b.text)) return alert("Заполните название и текст всех блоков");
        const newSong = {
            id: Date.now().toString(),
            title,
            language,
            sections: blocks.map(b => ({
                type: b.type,
                content: b.text.split(/(\s+)/).map(word => ({
                    text: word,
                    isSpace: /\s/.test(word)
                }))
            }))
        };
        const updatedSongs = [...songs, newSong];
        await saveToDatabase(updatedSongs);
        setIsAdding(false);
        setTitle('');
        setBlocks([{type: 'verse', text: ''}]);
        setCurrentSong(newSong);
    };

    const saveToDatabase = async (data) => {
        await fetch(`${BASE_URL}/music`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(data)
        });
        setSongs(data);
    };

    const addChord = (sectionIdx, wordIdx) => {
        if (!showChords || isDeleteMode || isEditingText) return;
        const chord = prompt("Введите аккорд:");
        if (!chord) return;
        const updatedSong = JSON.parse(JSON.stringify(currentSong));
        updatedSong.sections[sectionIdx].content.splice(wordIdx, 0, {chord, isChord: true});
        setCurrentSong(updatedSong);
        saveToDatabase(songs.map(s => s.id === updatedSong.id ? updatedSong : s));
    };

    const deleteChord = (sectionIdx, chordIdx) => {
        if (window.confirm("Удалить этот аккорд?")) {
            const updatedSong = JSON.parse(JSON.stringify(currentSong));
            updatedSong.sections[sectionIdx].content.splice(chordIdx, 1);
            setCurrentSong(updatedSong);
            saveToDatabase(songs.map(s => s.id === updatedSong.id ? updatedSong : s));
        }
    };

    const goBack = () => {
        setCurrentSong(null);
        setIsAdding(false);
        setIsEditingText(false);
        setIsDeleteMode(false);
        setTitle('');
        setBlocks([{type: 'verse', text: ''}]);
    };

    const changeFontSize = () => setFontSize(prev => (prev >= 24 ? 14 : prev + 2));

    return (
        <div className="Music">
            {!currentSong && !isAdding && (
                <div className="Music_list_view">
                    <div className="Music_header_top">
                        <h1>Песни</h1>
                        <button className="add_song_main_btn" onClick={() => setIsAdding(true)}>+ Добавить</button>
                    </div>

                    {/* ПАНЕЛЬ ПОИСКА И ФИЛЬТРАЦИИ */}
                    <div className="Music_filter_bar">
                        <input
                            type="text"
                            placeholder="Поиск по названию..."
                            className="search_input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <select
                            className="filter_select"
                            value={filterLanguage}
                            onChange={(e) => setFilterLanguage(e.target.value)}
                        >
                            <option value="All">Все языки</option>
                            <option value="RU">RU</option>
                            <option value="GE">ქარ</option>
                        </select>
                    </div>

                    <div className="song_grid">
                        {filteredSongs.length > 0 ? (
                            filteredSongs.map(song => (
                                <div key={song.id} className="song_card" onClick={() => setCurrentSong(song)}>
                                    <span className="song_title">{song.title}</span>
                                    <span className="song_lang">{song.language}</span>
                                </div>
                            ))
                        ) : (
                            <p className="no_results">Ничего не найдено</p>
                        )}
                    </div>
                </div>
            )}

            {(isAdding || isEditingText) && (
                <div className="Music_main_full">
                    <button className="back_btn" onClick={goBack}>← Назад</button>
                    <div className="song_builder">
                        <h2>{isEditingText ? 'Редактирование' : 'Новая песня'}</h2>
                        <div className="builder_header">
                            <div className="builder_header_group">
                                <input type="text" placeholder="Название..." value={title} onChange={e => setTitle(e.target.value)}/>
                                <select className="Language_changer" value={language} onChange={e => setLanguage(e.target.value)}>
                                    <option value="RU">RU</option><option value="GE">ქარ</option>
                                </select>
                            </div>
                        </div>
                        <div className="blocks_container">
                            {blocks.map((block, index) => (
                                <div key={index} className="block_input_card">
                                    <div className="block_controls">
                                        <select value={block.type} onChange={e => updateBlock(index, 'type', e.target.value)}>
                                            <option value="verse">Куплет</option><option value="chorus">Припев</option><option value="bridge">Мост</option>
                                        </select>
                                        <button className="remove_btn" onClick={() => setBlocks(blocks.filter((_, i) => i !== index))}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></svg>
                                        </button>
                                    </div>
                                    <textarea value={block.text} onChange={e => updateBlock(index, 'text', e.target.value)} placeholder="Текст..."/>
                                </div>
                            ))}
                        </div>
                        <div className="builder_footer">
                            <button className="secondary_btn" onClick={addBlockField}>+ Блок</button>
                            <button className="primary_btn" onClick={isEditingText ? handleSaveEdit : handleCreateSong}>
                                Сохранить
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {currentSong && !isAdding && !isEditingText && (
                <div className="Music_main_full">
                    <div className="Music_toolbar">
                        <button className="back_btn" onClick={goBack}>← К списку</button>
                        <div className="toolbar_right">
                            <button className="tool_btn" onClick={handleStartEditing} title="Редактировать текст">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" /><path d="M13.5 6.5l4 4" /></svg>
                            </button>
                            <button className={`tool_btn ${isDeleteMode ? 'delete_active' : ''}`} onClick={() => setIsDeleteMode(!isDeleteMode)}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7l16 0"/><path d="M10 11l0 6"/><path d="M14 11l0 6"/><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"/><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"/></svg>
                            </button>
                            <button className="tool_btn" onClick={changeFontSize}>
                                <small>A </small>A <span className={"tool_btn_last"}>{fontSize}</span>
                            </button>
                            <button className={`tool_btn ${!showChords ? 'off' : ''}`} onClick={() => setShowChords(!showChords)}>
                                {showChords ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M13 17a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M9 17v-13h10v13"/><path d="M9 8h10"/></svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17a3 3 0 1 0 6 0a3 3 0 1 0 -6 0"/><path d="M14.42 14.45a3 3 0 1 0 4.138 4.119"/><path d="M9 17v-8m0 -4v-1h10v11"/><path d="M12 8h7"/><path d="M3 3l18 18"/></svg>
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="song_viewer">
                        <header className="song_header"><h1>{currentSong.title}</h1></header>
                        {currentSong.sections.map((section, sIdx) => (
                            <div key={sIdx} className={`song_section ${section.type} ${section.type === 'chorus' ? 'chorus' : ''}`}>
                                <div className="section_body" style={{ fontSize: `${fontSize}px`, lineHeight: showChords ? '3' : 'normal' }}>
                                    {section.content.map((item, wIdx) => (
                                        <span key={wIdx} className="word_node">
                                            {item.isChord ? (
                                                showChords && (
                                                    <div className="chord_item">
                                                        {item.chord}
                                                        {isDeleteMode && (
                                                            <button className="del_chord_btn" onClick={() => deleteChord(sIdx, wIdx)}>×</button>
                                                        )}
                                                    </div>
                                                )
                                            ) : (
                                                <span className={item.isSpace ? "space" : "text"}
                                                      onClick={() => addChord(sIdx, wIdx)}>
                                                    {item.text}
                                                </span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Music;